import {useMemo, useState} from 'react'
import {IconBinaryTree2, IconSearch, IconUserPlus} from '@tabler/icons-react'
import {
  firstNote, fullName, isDeceased, jobsOf, kidsOf, lifespan, nameMatches, parentsOf, placesOf,
  searchWords, shortName, surnameRoot,
} from './api/tree'
import Portrait from './Portrait'
import type {TreeIndex, Person} from './api/types'

interface Props {
  ix: TreeIndex
  onEdit: (h: string) => void
  onShowInTree: (h: string) => void
  onAdd: () => void
}

type Sort = 'name' | 'birth'

export default function PeopleList({ix, onEdit, onShowInTree, onAdd}: Props) {
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<Sort>('name')
  const [onlyGaps, setOnlyGaps] = useState(false)
  const [onlyAlive, setOnlyAlive] = useState(false)
  const [clan, setClan] = useState('')

  /** Роды — по фамилии, приведённой к мужской форме. Мягков и Мягкова —
   *  один род, и без приведения семья распадалась на две записи в списке.
   *  Подпись рода — мужская форма: так род и называют. */
  const clans = useMemo(() => {
    const c = new Map<string, number>()
    for (const p of ix.people.values()) {
      const s = p.profile?.name_surname?.trim()
      if (!s) continue
      const root = surnameRoot(s)
      c.set(root, (c.get(root) || 0) + 1)
    }
    return [...c.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'ru'))
  }, [ix])

  const rows = useMemo(() => {
    const v = q.trim().toLowerCase()
    let list = [...ix.people.values()]
    if (v) { const words = searchWords(q); list = list.filter(p => nameMatches(p, words)) }
    if (clan) list = list.filter(p => surnameRoot((p.profile?.name_surname || '').trim()) === clan)
    // «неполные» — те, у кого нет ни одной даты: именно их и надо дозаполнять
    if (onlyGaps) list = list.filter(p => !lifespan(p))
    // «живые» — те, у кого нет события смерти. Строго говоря это «смерть не
    // записана»: у части стариков события просто нет. Осторожная сторона
    // здесь именно такая — лучше показать лишнего, чем спрятать живого.
    if (onlyAlive) list = list.filter(p => !isDeceased(p))
    const year = (p: Person) => Number((p.profile?.birth?.date || '').match(/\d{4}/)?.[0] || 0)
    list.sort((a, b) =>
      sort === 'name'
        ? fullName(a).localeCompare(fullName(b), 'ru')
        : (year(a) || 9999) - (year(b) || 9999),
    )
    return list
  }, [ix, q, sort, onlyGaps, onlyAlive, clan])

  const gaps = useMemo(() => [...ix.people.values()].filter(p => !lifespan(p)).length, [ix])
  const alive = useMemo(() => [...ix.people.values()].filter(p => !isDeceased(p)).length, [ix])

  return (
    <div className="page">
      <div className="pagehead">
        <h1>Люди</h1>
        <div className="muted">
          {rows.length === ix.people.size
            ? `${ix.people.size} человек в древе`
            : `${rows.length} из ${ix.people.size}`}
        </div>
        <button className="btn primary" onClick={onAdd}>
          <IconUserPlus size={15} stroke={1.7} /> Добавить человека
        </button>
      </div>

      <div className="toolbar">
        <div className="search wide">
          <IconSearch size={15} className="ico" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Фамилия или имя" />
        </div>
        <select className="sel" value={clan} onChange={e => setClan(e.target.value)} title="Показать один род">
          <option value="">Все роды</option>
          {clans.map(([name, n]) => (
            <option key={name} value={name}>{name} — {n}</option>
          ))}
        </select>
        <div className="seg">
          <button className={sort === 'name' ? 'on' : ''} onClick={() => setSort('name')}>По алфавиту</button>
          <button className={sort === 'birth' ? 'on' : ''} onClick={() => setSort('birth')}>По году рождения</button>
        </div>
        <button
          className={'btn' + (onlyAlive ? ' active' : '')}
          onClick={() => setOnlyAlive(v => !v)}
          title="Показать тех, у кого не записана смерть"
        >
          Только живые <em>{alive}</em>
        </button>
        <button
          className={'btn' + (onlyGaps ? ' active' : '')}
          onClick={() => setOnlyGaps(v => !v)}
          title="Показать тех, у кого не заполнено ни одной даты"
        >
          Без дат <em>{gaps}</em>
        </button>
      </div>

      <div className="rows">
        {rows.map(p => {
          const life = lifespan(p)
          return (
            <div
              className="prow clickable"
              key={p.handle}
              onClick={() => onEdit(p.handle)}
              title="Открыть карточку"
            >
              <Portrait p={p} size={106} shape="rounded" />
              <div className="who">
                <div className="n">
                  {fullName(p)}
                  {life ? <span className="yrs">{life}</span>
                        : <span className="yrs none">даты не заполнены</span>}
                </div>
                {placesOf(ix, p.handle).length > 0 && (
                  <div className="d"><b>Место:</b> {placesOf(ix, p.handle).join(" · ")}</div>
                )}
                <Facts ix={ix} p={p} onGo={onShowInTree} />
              </div>
              {/* Заметка справа: в строке остаётся много пустого места,
                  а именно в заметках лежит то, чего нет в полях, — как
                  человека звали в семье, кем работал, откуда родом. */}
              {firstNote(ix, p.handle) && (
                <div className="pnote">{firstNote(ix, p.handle)}</div>
              )}
              <button className="icobtn" title="Показать в древе"
                      onClick={e => { e.stopPropagation(); onShowInTree(p.handle) }}>
                <IconBinaryTree2 size={16} stroke={1.7} />
              </button>
            </div>
          )
        })}
        {!rows.length && <div className="empty">Никого не нашлось</div>}
      </div>
    </div>
  )
}

/** Родители, дети и места работы прямо в строке: список нужен, чтобы
 *  видеть пробелы не открывая каждого. Пустые разделы не рисуем — иначе
 *  строка забита прочерками, а их в этих данных больше, чем фактов. */
function Facts({ix, p, onGo}: {ix: TreeIndex; p: Person; onGo: (h: string) => void}) {
  const parents = parentsOf(ix, p.handle)
  const kids = kidsOf(ix, p.handle)
  const jobs = jobsOf(ix, p.handle)
  if (!parents.length && !kids.length && !jobs.length) return null

  const names = (list: string[]) =>
    list.map(h => (
      <button type="button" className="lnk" key={h}
              onClick={e => { e.stopPropagation(); onGo(h) }}>
        <Portrait p={ix.people.get(h)} size={20} />
        {shortName(ix.people.get(h))}
      </button>
    ))

  return (
    <div className="facts">
      {parents.length > 0 && <div><b>Родители:</b> {names(parents)}</div>}
      {kids.length > 0 && <div><b>Дети:</b> {names(kids)}</div>}
      {jobs.length > 0 && <div><b>Работа:</b> {jobs.join(" · ")}</div>}
    </div>
  )
}
